import { Injectable, inject } from '@angular/core';

import type {
  AddressAutocompleteProvider,
  AddressSearchOptions,
  AddressSuggestion,
} from '@arya/shared/address-autocomplete-contract';

import { NOMINATIM_USER_AGENT } from './nominatim-user-agent';

interface NominatimResponseItem {
  readonly display_name?: string;
  readonly lat?: string;
  readonly lon?: string;
  readonly address?: NominatimAddress;
}

interface NominatimAddress {
  readonly road?: string;
  readonly pedestrian?: string;
  readonly footway?: string;
  readonly house_number?: string;
  readonly city?: string;
  readonly town?: string;
  readonly village?: string;
  readonly hamlet?: string;
  readonly municipality?: string;
  readonly state?: string;
  readonly region?: string;
  readonly county?: string;
  readonly postcode?: string;
  readonly country?: string;
  readonly country_code?: string;
}

const NOMINATIM_ENDPOINT = 'https://nominatim.openstreetmap.org/search';
const DEFAULT_LIMIT = 5;

@Injectable({ providedIn: 'root' })
export class NominatimAddressAutocompleteProvider implements AddressAutocompleteProvider {
  private readonly userAgent = inject(NOMINATIM_USER_AGENT);
  public readonly available = true;

  public async search(query: string, options?: AddressSearchOptions): Promise<readonly AddressSuggestion[]> {
    const trimmed = query.trim();
    if (trimmed.length === 0) return [];
    const url = this.buildUrl(trimmed, options);
    const response = await this.fetchSafely(url, options?.signal);
    if (response === undefined) return [];
    const items = await this.parseSafely(response);
    return items.map((item) => this.toSuggestion(item)).filter((s): s is AddressSuggestion => s !== undefined);
  }

  private buildUrl(query: string, options: AddressSearchOptions | undefined): string {
    const params = new URLSearchParams({
      format: 'jsonv2',
      addressdetails: '1',
      limit: String(options?.limit ?? DEFAULT_LIMIT),
      q: query,
    });
    const countryHint = options?.countryHint?.trim();
    if (countryHint !== undefined && countryHint.length > 0) {
      params.set('countrycodes', countryHint.toLowerCase());
    }
    return `${NOMINATIM_ENDPOINT}?${params.toString()}`;
  }

  private async fetchSafely(url: string, signal: AbortSignal | undefined): Promise<Response | undefined> {
    try {
      const response = await fetch(url, {
        headers: { 'User-Agent': this.userAgent, Accept: 'application/json' },
        signal,
      });
      return response.ok ? response : undefined;
    } catch {
      return undefined;
    }
  }

  private async parseSafely(response: Response): Promise<readonly NominatimResponseItem[]> {
    try {
      const data: unknown = await response.json();
      if (!Array.isArray(data)) return [];
      return data.filter((item): item is NominatimResponseItem => typeof item === 'object' && item !== null);
    } catch {
      return [];
    }
  }

  private toSuggestion(item: NominatimResponseItem): AddressSuggestion | undefined {
    const { address } = item;
    if (address === undefined) return undefined;
    const street = this.composeStreet(address);
    const city = address.city ?? address.town ?? address.village ?? address.hamlet ?? address.municipality ?? '';
    const region = address.state ?? address.region ?? address.county ?? '';
    const postalCode = address.postcode ?? '';
    const countryCode = address.country_code?.toUpperCase() ?? '';
    const countryName = address.country ?? '';
    if (street.length === 0 && city.length === 0) return undefined;
    return {
      displayText: [street, city].filter((p) => p.length > 0).join(', '),
      street,
      city,
      region,
      postalCode,
      countryCode,
      countryName,
      latitude: this.toNumber(item.lat),
      longitude: this.toNumber(item.lon),
    };
  }

  private composeStreet(address: NominatimAddress): string {
    const road = address.road ?? address.pedestrian ?? address.footway ?? '';
    const houseNumber = address.house_number ?? '';
    return [road, houseNumber].filter((p) => p.length > 0).join(' ');
  }

  private toNumber(value: string | undefined): number | undefined {
    if (value === undefined) return undefined;
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
}
